import type { ActionSummary, Workspace } from "../api/contract";
import type { PublicationState } from "../hooks/usePublication";
import { hasHomePage, latestPublication, liveStatus } from "./workspaceState";

/** The top bar's one publish button, always named for what tapping it does next. */
export interface PublishControl {
  label: string;
  /** The phone label, when it differs. */
  shortLabel?: string;
  disabled: boolean;
  onClick: () => void;
  /** A review or publish is under way: a spinner shows before the label. */
  busy?: boolean;
  /** "live": nothing new to publish, so the button is quiet and opens the live panel. */
  tone: "action" | "live";
}

export interface PublishControlActions {
  start: () => void;
  /** Reopens a prepared review the owner left, when there is one. */
  resumeWaiting: (() => void) | null;
  /** Brings the publish panel forward. */
  openPanel: () => void;
  /** Opens the live panel: visit, copy, domain, earlier versions. */
  openLive: () => void;
  /** Prepares a fresh review after a failed publish. */
  retry: () => void;
}

/** The last publication that finished failed, so the live site didn't change. */
export function lastPublishFailed(workspace: Workspace): ActionSummary | null {
  const last = latestPublication(workspace, (action) => action.state !== "awaiting-confirmation" && action.state !== "cancelled");
  return last?.state === "failed" ? last : null;
}

function publishLabel(changeCount: number): { label: string; shortLabel?: string } {
  if (changeCount === 0) return { label: "Publish changes", shortLabel: "Publish" };
  return { label: `Publish ${changeCount} ${changeCount === 1 ? "change" : "changes"}`, shortLabel: `Publish ${changeCount}` };
}

export function publishControl(
  workspace: Workspace,
  state: PublicationState,
  options: {
    /** The last workspace read failed transiently. */
    disconnected: boolean;
    /** Viewing an earlier version blocks publishing. */
    versionBlocked: boolean;
    /** Unpublished changes that go live with the next publish. */
    changeCount: number;
  },
  actions: PublishControlActions,
): PublishControl {
  const live = liveStatus(workspace);
  const next = live.kind === "never-published" ? { label: "Publish" } : publishLabel(options.changeCount);
  switch (state.phase) {
    case "preparing":
      return { ...next, tone: "action", disabled: false, busy: true, onClick: actions.openPanel };
    case "publishing":
    case "confirming":
      return { label: "Publishing…", shortLabel: "Publishing", tone: "action", disabled: false, busy: true, onClick: actions.openPanel };
    case "settled":
      if (state.action.state === "failed") return { label: "Try again", tone: "action", disabled: options.disconnected, onClick: actions.retry };
      if (live.kind === "matches-draft") return { label: "Live", tone: "live", disabled: false, onClick: actions.openPanel };
      return { ...next, tone: "action", disabled: false, onClick: actions.openPanel };
    case "idle":
      break;
    default:
      return { ...next, tone: "action", disabled: false, onClick: actions.openPanel };
  }
  if (actions.resumeWaiting) return { ...next, tone: "action", disabled: options.versionBlocked, onClick: actions.resumeWaiting };
  const blocked = options.disconnected || options.versionBlocked || !hasHomePage(workspace.pages);
  if (lastPublishFailed(workspace) && live.kind !== "matches-draft") return { label: "Try again", tone: "action", disabled: blocked, onClick: actions.start };
  if (live.kind === "matches-draft") return { label: "Live", tone: "live", disabled: false, onClick: actions.openLive };
  return { ...next, tone: "action", disabled: blocked, onClick: actions.start };
}

/** The line under the site name: where the site stands, in the owner's words. */
export interface PublishStatusLine {
  text: string;
  /** On phones: a few words, no address. */
  short: string;
  tone: "not-live" | "pending" | "live" | "progress" | "danger";
  /** The address leads the line, linking to the live site once there is one. */
  withAddress: boolean;
}

/**
 * With `websiteOnly`, a site that also has a blog says "website changes": posts publish on their own, so the
 * Website's count is never about them.
 */
export function publishStatusLine(workspace: Workspace, state: PublicationState, changeCount: number, options: { websiteOnly?: boolean } = {}): PublishStatusLine {
  if (state.phase === "publishing" || state.phase === "confirming") return { text: "Going live…", short: "Going live…", tone: "progress", withAddress: false };
  const live = liveStatus(workspace);
  const failed = state.phase === "settled" ? state.action.state === "failed" : state.phase === "idle" && lastPublishFailed(workspace) !== null && live.kind !== "matches-draft";
  if (failed) return { text: live.kind === "never-published" ? "Didn’t go live" : "Last publish didn’t finish · live site unchanged", short: "Didn’t go live", tone: "danger", withAddress: false };
  if (live.kind === "never-published") return { text: "not live yet", short: "Not live yet", tone: "not-live", withAddress: true };
  if (live.kind === "has-changes") {
    const noun = options.websiteOnly ? "website change" : "change";
    const changes = changeCount ? `${changeCount} ${noun}${changeCount === 1 ? "" : "s"} not live` : options.websiteOnly ? "Website changes not live" : "Changes not live";
    return { text: `${changes.charAt(0).toLowerCase()}${changes.slice(1)} yet`, short: changes, tone: "pending", withAddress: true };
  }
  return { text: "live", short: "Live", tone: "live", withAddress: true };
}

/** When the site last went live, as "just now", "5 min ago", "3 h ago" or "2 days ago". */
export function publishedAgo(workspace: Workspace, now = Date.now()): string | null {
  const serving = workspace.actions.find((action) => action.state === "succeeded" && action.result?.releaseId === workspace.site.activeReleaseId);
  if (!serving?.createdAt) return null;
  const minutes = Math.max(0, Math.round((now - Date.parse(serving.createdAt)) / 60_000));
  if (Number.isNaN(minutes)) return null;
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}
